import "./Finish.css";
import { Button, Col, Divider, Row, Spin, Tooltip, message } from "antd";
import { QuestionCircleOutlined } from '@ant-design/icons';
import ImageGallery from "./ImageGallery/ImageGallery";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectSessionId } from "../../../store/sessionSlice";
import { fetchRequest } from "../../../utils/Fetcher";
import { AttachSignedUrls, AttachVideoSignedUrls } from "../../../utils/GetSignedURL";
import { getVideoThumbnail } from "../../../utils/GenerateVideoThumbnail";
import { DownloadAsZip } from "../../../utils/DownloadAsZip";
import { ExportSessionBundleZip } from "../../../utils/ExportSessionBundle";

const Finish = () => {
  const desc = "Finish";
  const sessionId = useSelector(selectSessionId);

  const [messageApi, contextHolder] = message.useMessage();

  const [isLoading, setIsLoading] = useState(true);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [videoGenerated, setVideoGenerated] = useState(null);
  const [posterUrl, setPosterUrl] = useState(null);
  const [posterLoading, setPosterLoading] = useState(false);
  const [exportingImages, setExportingImages] = useState(false);
  const [exportingVideo, setExportingVideo] = useState(false);
  const [exportingAllData, setExportingAllData] = useState(false);

  const thumbCacheRef = useRef(new Map());
  const cancelledRef = useRef(false);

  const fetchSessionSummary = async () => {
    const { result } = await fetchRequest({
      method: "GET",
      path: `status/sessions/${sessionId}/summary`,
    });
    return result;
  };

  const isCancelled = () => cancelledRef.current;

  const setIfAlive = (fn) => {
    if (!isCancelled()) fn();
  };

  const pickLatestMergedSucceeded = (videos = []) =>
    (videos ?? [])
      .filter((v) => v?.type === "merged" && v?.state === "SUCCEEDED")
      .sort(
        (a, b) =>
          new Date(b?.finishedAt ?? b?.createdAt ?? 0) -
          new Date(a?.finishedAt ?? a?.createdAt ?? 0)
      )[0] || null;

  const loadImages = async (summary) => {
    const imgSelectedOptions = summary?.campaign_images?.items?.entries ?? [];
    const withSignedImgs = await AttachSignedUrls(imgSelectedOptions, 1);
    return withSignedImgs.filter((x) => {
      const signedUrl =
        x?.signed_url?.signed_url ??
        x?.signed_url ??
        x?.url?.signed_url ??
        x?.url;
      return !!signedUrl;
    });
  };

  const loadMergedVideoWithUrl = async (summary) => {
    const generatedVid = summary?.videos?.items?.entries ?? [];
    const mergedLatest = pickLatestMergedSucceeded(generatedVid);
    if (!mergedLatest) return null;

    const [mergedWithUrl] = await AttachVideoSignedUrls([mergedLatest], 1);
    return mergedWithUrl ?? null;
  };

  const ensurePosterForUrl = async (url) => {
    if (!url) {
      setIfAlive(() => setPosterUrl(null));
      return;
    }

    const cache = thumbCacheRef.current;
    if (cache.has(url)) {
      setIfAlive(() => setPosterUrl(cache.get(url)));
      return;
    }

    setIfAlive(() => setPosterLoading(true));

    try {
      const thumb = await getVideoThumbnail(url, { seekTo: 0.1, width: 1280 });
      if (isCancelled()) return;

      cache.set(url, thumb);
      setPosterUrl(thumb);
    } catch (e) {
      setIfAlive(() => setPosterUrl(null));
    } finally {
      setIfAlive(() => setPosterLoading(false));
    }
  };

  const forceDownload = async (url, filename) => {
    const res = await fetch(url, { method: "GET" });
    if (!res.ok) throw new Error(`Download failed: ${res.status}`);

    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(blobUrl);
  };

  const handleExportAllImages = async () => {
    if (!generatedImages?.length || exportingImages) return;

    try {
      setExportingImages(true);

      const items = (generatedImages ?? [])
        .map((it, idx) => {
          const signedUrl =
            it?.signed_url?.signed_url ??
            it?.signed_url ??
            it?.url?.signed_url ??
            it?.url ??
            "";

          return {
            key: String(idx),
            signedUrl,
            filename: `generated_${idx + 1}.jpg`,
            raw: it,
          };
        })
        .filter((x) => !!x.signedUrl);

      if (!items.length) {
        messageApi.error("No signed image URLs available");
        return;
      }

      await DownloadAsZip(items, {
        zipName: `generated_images_${sessionId || "session"}.zip`,
      });
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || "Failed to export images");
    } finally {
      setExportingImages(false);
    }
  };

  const handleExportVideo = async () => {
    const videoUrl = videoGenerated?.url?.signed_url ?? videoGenerated?.url;
    if (!videoUrl || exportingVideo) return;

    try {
      setExportingVideo(true);
      await forceDownload(
        videoUrl,
        `merged_video_${sessionId || "session"}.mp4`
      );
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || "Failed to export video");
    } finally {
      setExportingVideo(false);
    }
  };

  const handleExportAllData = async () => {
    if (!sessionId || exportingAllData) return;

    try {
      setExportingAllData(true);

      const summary = await fetchSessionSummary();
      const categories = summary?.categories ?? {};

      await ExportSessionBundleZip({
        categories,
        sessionId,
        generatedImages,
        mergedVideo: videoGenerated,
      });

      messageApi.success("All data exported");
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || "Failed to export all data");
    } finally {
      setExportingAllData(false);
    }
  };

  useEffect(() => {
    if (!sessionId) return;

    setIsLoading(true);

    cancelledRef.current = false;

    const load = async () => {
      try {
        const summary = await fetchSessionSummary();
        const summaryData = summary.categories;

        const okImgs = await loadImages(summaryData);
        setIfAlive(() => setGeneratedImages(okImgs));

        const merged = await loadMergedVideoWithUrl(summaryData);
        if (!merged) {
          setIfAlive(() => {
            setVideoGenerated(null);
            setPosterUrl(null);
            setPosterLoading(false);
          });
          return;
        }

        setIfAlive(() => setVideoGenerated(merged));
        await ensurePosterForUrl(merged?.url?.signed_url ?? merged?.url);
      } catch (e) {
        setIfAlive(() => setPosterLoading(false));
        console.error(e);
      } finally {
        setIfAlive(() => setIsLoading(false)); // ← pindah ke sini
      }
    };

    load();

    return () => {
      cancelledRef.current = true;
    };
  }, [sessionId]);

  return (
    <div>
      {contextHolder}

      <div className="content-title-container">
        <div className="content-title">
          {desc}
          <Tooltip placement="bottom" title={process.env.REACT_APP_TOOLTIP_FINISH}>
              <QuestionCircleOutlined style={{ marginLeft: 8, fontSize: 14, color: '#888', cursor: 'pointer' }} />
          </Tooltip>
        </div>
        <Divider className="content-title-divider" />
      </div>

      <div className="finish-container">
        <div className="export-all-container">
          <div>
            Export all data including customer persona, campaign concept, storyboard and all media generated
          </div>
          <Button
            onClick={handleExportAllData}
            loading={exportingAllData}
            disabled={!sessionId || exportingAllData}
          >
            Export all data
          </Button>
        </div>

        <Row gutter={[16, 16]} className="generated-media-container">
          <Col xs={24} md={12} className="generated-image-container">
            <div className="media-export-button-container">
              <Button
                className="media-export-button"
                onClick={handleExportAllImages}
                loading={exportingImages}
                disabled={!generatedImages?.length || exportingImages}
              >
                Export all images
              </Button>
            </div>

            <ImageGallery generatedImages={generatedImages} loading={isLoading} />
          </Col>

          <Col xs={24} md={12}>
            <div className="media-export-button-container">
              <Button
                className="media-export-button"
                onClick={handleExportVideo}
                loading={exportingVideo}
                disabled={!videoGenerated?.url || exportingVideo}
              >
                Export video
              </Button>
            </div>

            <div className="video-box">
              {isLoading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                  <Spin />
                </div>
              ) : videoGenerated?.url ? (
                <video
                  className="video-player"
                  controls
                  src={videoGenerated?.url?.signed_url ?? videoGenerated?.url}
                  poster={posterUrl ?? undefined}
                />
              ) : (
                <div style={{ padding: 12 }}>
                  No merged video yet
                </div>
              )}
            </div>
          </Col>
        </Row>

        {posterLoading && (
          <div style={{ padding: 8, fontSize: 12, opacity: 0.7 }}>
            Generating preview...
          </div>
        )}
      </div>
    </div>
  );
};

export default Finish;